// src/clock/jobVisits.js
//
// Job check-in / check-out ("visits").
//
// A visit is one person's time at one job stop. Clock In / Clock Out (time_logs)
// still tracks the whole work day. A visit sits inside that day:
//
//   Clock In ──▶ Check In @ Job A ──▶ Check Out ──▶ Check In @ Job B ──▶ Check Out ──▶ Clock Out
//
// Collection: job_visits/{visitId}   (new; nothing existing is changed)
//   jobId, wo, siteName, address, city, state, zip, occurrenceDate ("YYYY-MM-DD")
//   userId, userName, timeLogId
//   status               "checked_in" | "checked_out"
//   checkInAt            Date
//   checkInLocation      {latitude, longitude, accuracy, timestamp}
//   checkInDistanceMeters  meters from the job address (null if the address
//                          could not be located)
//   jobLocation          {latitude, longitude} of the job address, if found
//   checkOutAt           Date | null
//   checkOutLocation     {latitude, longitude, accuracy, timestamp} | null
//   checkOutDistanceMeters
//   durationMinutes      whole minutes between check-in and check-out
//   checkOutReason       "user" | "switched_jobs" | "clock_out" | "job_completed"
//   createdAt / updatedAt  serverTimestamp

import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import moment from "moment";
import {
  addDoc,
  collection,
  doc,
  getDocs,
  onSnapshot,
  query,
  serverTimestamp,
  updateDoc,
  where,
} from "firebase/firestore";
import { db } from "../firebaseConfig";
import { getLocation, distanceInMeters } from "./useClockInLogic";

export const JOB_VISITS = "job_visits";

// A check-in further than this from the job address is flagged for the
// supervisor. It is NOT blocked: addresses geocode to the street, big
// properties are big, and phone GPS drifts.
export const FAR_FROM_JOB_METERS = 400;

// ---------------------------------------------------------------------------
// Pure helpers (no Firestore) — safe to unit test
// ---------------------------------------------------------------------------

export const toDateSafe = (v) => {
  if (!v) return null;
  if (typeof v.toDate === "function") return v.toDate();
  if (v instanceof Date) return v;
  if (typeof v === "object" && typeof v.seconds === "number") {
    return new Date(v.seconds * 1000);
  }
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
};

// Same rule as getCompletionOccurrenceKey in recurringOccurrenceUtils (the
// canonical occurrence date) so a visit lines up with the occurrence that
// gets completed, even after that occurrence was rescheduled.
export const getVisitOccurrenceKey = (job) => {
  const raw =
    job?.originalOccurrenceDate ||
    job?.occurrenceDate ||
    job?.startDate ||
    job?.__renderDate;
  if (!raw) return "";
  if (typeof raw === "object" && typeof raw.seconds === "number") {
    return moment(raw.seconds * 1000).format("YYYY-MM-DD");
  }
  if (typeof raw === "string") {
    const s = raw.slice(0, 10);
    if (moment(s, "YYYY-MM-DD", true).isValid()) return s;
  }
  const m = moment(raw);
  return m.isValid() ? m.format("YYYY-MM-DD") : "";
};

// "1:05:09" under a day; minutes-only text is used for totals.
export const formatElapsed = (ms) => {
  if (ms == null || Number.isNaN(ms) || ms < 0) return "0:00:00";
  const total = Math.floor(ms / 1000);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  return `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
};

export const formatMinutes = (mins) => {
  const n = Math.max(0, Math.round(Number(mins) || 0));
  const h = Math.floor(n / 60);
  const m = n % 60;
  if (h === 0) return `${m}m`;
  return `${h}h ${String(m).padStart(2, "0")}m`;
};

// Minutes a visit has lasted. Open visits count up to `now`.
export const visitMinutes = (visit, now = Date.now()) => {
  if (!visit) return 0;
  if (visit.status === "checked_out" && visit.durationMinutes != null) {
    return Number(visit.durationMinutes) || 0;
  }
  const start = toDateSafe(visit.checkInAt);
  if (!start) return 0;
  const end = toDateSafe(visit.checkOutAt)?.getTime() ?? now;
  return Math.max(0, Math.round((end - start.getTime()) / 60000));
};

export const isSameJobOccurrence = (visit, job) =>
  !!visit &&
  !!job &&
  visit.jobId === job.id &&
  (visit.occurrenceDate || "") === getVisitOccurrenceKey(job);

export const jobAddressText = (job) =>
  [job?.address, job?.city, job?.state, job?.zip].filter(Boolean).join(", ");

// Coordinates already stored on a job, if any (several historical shapes).
export const getStoredJobCoords = (job) => {
  if (!job) return null;
  const candidates = [
    [job.latitude, job.longitude],
    [job.lat, job.lng],
    [job.location?.latitude, job.location?.longitude],
    [job.location?.lat, job.location?.lng],
    [job.geo?.lat, job.geo?.lng],
  ];
  for (const [a, b] of candidates) {
    const lat = Number(a);
    const lng = Number(b);
    if (a != null && b != null && !Number.isNaN(lat) && !Number.isNaN(lng)) {
      return { latitude: lat, longitude: lng };
    }
  }
  return null;
};

export const mapsLinkFor = (loc) => {
  if (!loc) return null;
  const lat = Number(loc.latitude ?? loc.lat);
  const lng = Number(loc.longitude ?? loc.lng);
  if (Number.isNaN(lat) || Number.isNaN(lng)) return null;
  return `https://www.google.com/maps?q=${lat},${lng}`;
};

// ---------------------------------------------------------------------------
// Job address -> coordinates (best effort, never blocks a check-in)
// ---------------------------------------------------------------------------
const geocodeCache = {};

const geocodeJob = (job, timeoutMs = 6000) =>
  new Promise((resolve) => {
    const stored = getStoredJobCoords(job);
    if (stored) return resolve(stored);

    const address = jobAddressText(job);
    if (!address) return resolve(null);
    if (geocodeCache[address] !== undefined) return resolve(geocodeCache[address]);

    const Geocoder = typeof window !== "undefined" && window.google?.maps?.Geocoder;
    if (!Geocoder) return resolve(null);

    let done = false;
    const timer = setTimeout(() => {
      if (!done) {
        done = true;
        resolve(null);
      }
    }, timeoutMs);

    try {
      new Geocoder().geocode({ address }, (results, status) => {
        if (done) return;
        done = true;
        clearTimeout(timer);
        if (status === "OK" && results?.[0]) {
          const loc = results[0].geometry.location;
          const coords = { latitude: loc.lat(), longitude: loc.lng() };
          geocodeCache[address] = coords;
          resolve(coords);
        } else {
          geocodeCache[address] = null;
          resolve(null);
        }
      });
    } catch (e) {
      done = true;
      clearTimeout(timer);
      resolve(null);
    }
  });

const distanceTo = (point, target) => {
  if (!point || !target) return null;
  return Math.round(
    distanceInMeters(
      Number(point.latitude),
      Number(point.longitude),
      Number(target.latitude),
      Number(target.longitude)
    )
  );
};

// ---------------------------------------------------------------------------
// Firestore writes
// ---------------------------------------------------------------------------

// Check out one open visit. GPS is attempted but never required.
export const checkOutVisit = async (visit, reason = "user", gpsIn) => {
  if (!visit?.id) return null;
  let gps = gpsIn;
  if (gps === undefined) {
    try {
      gps = await getLocation();
    } catch (err) {
      console.warn("Check-out without GPS:", err);
      gps = null;
    }
  }

  const now = new Date();
  const start = toDateSafe(visit.checkInAt) || now;
  const durationMinutes = Math.max(0, Math.round((now - start) / 60000));
  const point = gps ? { ...gps, timestamp: now.getTime() } : null;

  const update = {
    status: "checked_out",
    checkOutAt: now,
    checkOutLocation: point,
    checkOutDistanceMeters: point ? distanceTo(point, visit.jobLocation) : null,
    durationMinutes,
    checkOutReason: reason,
    updatedAt: serverTimestamp(),
  };
  await updateDoc(doc(db, JOB_VISITS, visit.id), update);
  return { ...visit, ...update, updatedAt: now };
};

// Used by Clock Out: closes anything still open for this person so a job
// timer can never run overnight.
export const closeOpenVisitsForUser = async (userId, reason = "clock_out", gps) => {
  if (!userId) return 0;
  const snap = await getDocs(
    query(collection(db, JOB_VISITS), where("userId", "==", userId))
  );
  const open = snap.docs
    .map((d) => ({ id: d.id, ...d.data() }))
    .filter((v) => v.status === "checked_in");
  await Promise.all(open.map((v) => checkOutVisit(v, reason, gps ?? null)));
  return open.length;
};

// ---------------------------------------------------------------------------
// Hook used on Home (tech + staff)
// ---------------------------------------------------------------------------
// clock: the object returned by useClockInLogic()
export function useJobVisits(clock) {
  const userId = clock?.userId || null;
  const [visits, setVisits] = useState([]);
  const [busyKey, setBusyKey] = useState(null); // jobId_occurrence being written
  const [error, setError] = useState(null);
  const clockRef = useRef(clock);
  useEffect(() => {
    clockRef.current = clock;
  }, [clock]);

  useEffect(() => {
    if (!userId) {
      setVisits([]);
      return undefined;
    }
    // Single equality filter: no composite index needed.
    const unsub = onSnapshot(
      query(collection(db, JOB_VISITS), where("userId", "==", userId)),
      (snap) => setVisits(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
      (err) => {
        console.warn("job_visits listen failed:", err);
        setError("Could not load your job check-ins.");
      }
    );
    return () => unsub();
  }, [userId]);

  const openVisit = useMemo(() => {
    const open = visits.filter((v) => v.status === "checked_in");
    if (open.length <= 1) return open[0] || null;
    // Should not happen, but if it does show the newest.
    return open.sort(
      (a, b) => (toDateSafe(b.checkInAt)?.getTime() || 0) - (toDateSafe(a.checkInAt)?.getTime() || 0)
    )[0];
  }, [visits]);

  const todaysVisits = useMemo(() => {
    const today = moment().format("YYYY-MM-DD");
    return visits
      .filter((v) => {
        const d = toDateSafe(v.checkInAt);
        return d && moment(d).format("YYYY-MM-DD") === today;
      })
      .sort(
        (a, b) => (toDateSafe(a.checkInAt)?.getTime() || 0) - (toDateSafe(b.checkInAt)?.getTime() || 0)
      );
  }, [visits]);

  const visitsForJob = useCallback(
    (job) => visits.filter((v) => isSameJobOccurrence(v, job)),
    [visits]
  );

  const hasVisitFor = useCallback(
    (job) => visits.some((v) => isSameJobOccurrence(v, job)),
    [visits]
  );

  const isCheckedInTo = useCallback(
    (job) => !!openVisit && isSameJobOccurrence(openVisit, job),
    [openVisit]
  );

  const checkIn = useCallback(
    async (job) => {
      const c = clockRef.current;
      if (!job?.id || !c?.userId) return false;
      setError(null);

      if (!c.isClockedIn) {
        alert("Clock in for the day first, then check in to this job.");
        return false;
      }

      if (openVisit) {
        if (isSameJobOccurrence(openVisit, job)) return true;
        const ok = window.confirm(
          `You are still checked in at ${openVisit.siteName || "another job"}` +
            `${openVisit.wo ? ` (WO #${openVisit.wo})` : ""}.\n\n` +
            "Check out of that job and check in here?"
        );
        if (!ok) return false;
      }

      const key = `${job.id}_${getVisitOccurrenceKey(job)}`;
      setBusyKey(key);
      try {
        let gps;
        try {
          gps = await getLocation();
        } catch (err) {
          alert(`Check-in needs your location.\n\n${err.message}`);
          setError(err.message);
          return false;
        }

        if (openVisit) {
          await checkOutVisit(openVisit, "switched_jobs", gps);
        }

        const now = new Date();
        const point = { ...gps, timestamp: now.getTime() };
        const jobLocation = await geocodeJob(job);
        const dist = distanceTo(point, jobLocation);

        await addDoc(collection(db, JOB_VISITS), {
          jobId: job.id,
          wo: job.wo ?? null,
          siteName: job.siteName || "",
          address: job.address || "",
          city: job.city || "",
          state: job.state || "",
          zip: job.zip || "",
          serviceType: job.serviceType || "",
          occurrenceDate: getVisitOccurrenceKey(job),
          userId: c.userId,
          userName: c.userName || "",
          timeLogId: c.currentLogId || null,
          status: "checked_in",
          checkInAt: now,
          checkInLocation: point,
          jobLocation: jobLocation || null,
          checkInDistanceMeters: dist,
          checkOutAt: null,
          checkOutLocation: null,
          checkOutDistanceMeters: null,
          durationMinutes: null,
          checkOutReason: null,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });

        if (dist != null && dist > FAR_FROM_JOB_METERS) {
          alert(
            `Checked in. Note: your phone shows you about ${Math.round(dist)} m ` +
              `(${(dist / 1609.34).toFixed(2)} mi) from the job address. ` +
              "Your supervisor will see this."
          );
        }
        return true;
      } catch (err) {
        console.error("Check-in failed:", err);
        setError("Check-in failed. Please try again.");
        alert("Check-in failed. Please try again.");
        return false;
      } finally {
        setBusyKey(null);
      }
    },
    [openVisit]
  );

  const checkOut = useCallback(
    async (reason = "user") => {
      if (!openVisit) return false;
      setBusyKey(`${openVisit.jobId}_${openVisit.occurrenceDate || ""}`);
      try {
        await checkOutVisit(openVisit, reason);
        return true;
      } catch (err) {
        console.error("Check-out failed:", err);
        alert("Check-out failed. Please try again.");
        return false;
      } finally {
        setBusyKey(null);
      }
    },
    [openVisit]
  );

  // Called after a job is marked completed: if they are checked in to it,
  // check them out automatically.
  const checkOutIfAt = useCallback(
    async (job, reason = "job_completed") => {
      if (!openVisit || !isSameJobOccurrence(openVisit, job)) return false;
      try {
        await checkOutVisit(openVisit, reason);
        return true;
      } catch (err) {
        console.warn("Auto check-out failed:", err);
        return false;
      }
    },
    [openVisit]
  );

  const isBusyFor = useCallback(
    (job) => busyKey === `${job?.id}_${getVisitOccurrenceKey(job)}`,
    [busyKey]
  );

  return {
    visits,
    openVisit,
    todaysVisits,
    visitsForJob,
    hasVisitFor,
    isCheckedInTo,
    checkIn,
    checkOut,
    checkOutIfAt,
    isBusyFor,
    isBusy: !!busyKey,
    error,
  };
}

// Re-render every `ms` so running timers tick.
export const useNow = (ms = 1000, enabled = true) => {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!enabled) return undefined;
    const id = setInterval(() => setNow(Date.now()), ms);
    return () => clearInterval(id);
  }, [ms, enabled]);
  return now;
};

// One-off check used by pages that don't run the full useJobVisits hook
// (e.g. the Calendar page): has this user checked in to this occurrence?
export const userHasVisitForJob = async (userId, job) => {
  if (!userId || !job?.id) return false;
  const snap = await getDocs(
    query(collection(db, JOB_VISITS), where("userId", "==", userId))
  );
  return snap.docs.some((d) => isSameJobOccurrence({ id: d.id, ...d.data() }, job));
};
